import { FC, ReactNode, useState } from 'react';
import Highlighter from 'react-highlighter';
import { useTranslation } from 'react-i18next';

import { QuestionCardSkeleton } from './QuestionCardSkeleton';
import { format, parseISO } from 'date-fns';

import {
  CalendarIcon,
  DeleteIcon,
  EditIcon,
  GlobeIcon,
  MoreVerticalIcon,
  SmileIcon,
} from '@/assets/icons';
import { Card, IconButton } from '@/components/atoms';
import { CardContent } from '@/components/atoms/Card/Card';
import { Tag, TagProps } from '@/components/atoms/Tag/Tag';
import {
  Dropdown,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuSimpleItem,
  DropdownMenuTrigger,
} from '@/components/organisms';

const formatLastAskedDate = (date: string | null) => {
  if (!date) return null;

  return format(parseISO(date), 'dd MMM, yyyy');
};

export type QuestionCardOption = {
  id: string;
  icon: ReactNode;
  label: string;
  tag?: TagProps;
};

type QuestionCardProps = {
  text: string;
  score: number | null;
  lastAsked: string | null;
  isLoading?: boolean;
  searchText?: string;
  onTranslationClick: () => void;
  onEditClick: () => void;
  onDeleteClick: () => void;
};

export const QuestionCard: FC<QuestionCardProps> = ({
  text,
  score,
  lastAsked,
  isLoading,
  searchText,
  onDeleteClick,
  onEditClick,
  onTranslationClick,
}) => {
  const { t } = useTranslation('components');

  const [isOpenDropdown, setIsOpenDropdown] = useState(false);

  if (isLoading) {
    return <QuestionCardSkeleton />;
  }

  const handleTransaltionClick = () => {
    onTranslationClick();
    setIsOpenDropdown(false);
  };
  const handleEditClick = () => {
    onEditClick();
    setIsOpenDropdown(false);
  };
  const handleDeleteClick = () => {
    onDeleteClick();
    setIsOpenDropdown(false);
  };

  return (
    <Card className="rounded-[12px] p-4">
      <CardContent className="flex items-start gap-4 justify-between">
        <div>
          <p className="text-body-14-semibold text-custom-warm-grey-800 mb-2">
            <Highlighter search={searchText ?? ''}>{text}</Highlighter>
          </p>
          <div className="flex items-center gap-4">
            {lastAsked ? (
              <div className="flex items-center gap-1.5 min-h-[22px]">
                <CalendarIcon width={16} height={16} className="text-custom-warm-grey-400" />
                <p className="text-body-12-regular text-custom-warm-grey-600">
                  {t('categoryCard.lastAskedText')} {formatLastAskedDate(lastAsked)}
                </p>
              </div>
            ) : (
              <p className="text-body-12-regular text-custom-warm-grey-600">
                {t('categoryCard.notAskedText')}
              </p>
            )}
            {score !== null && (
              <div className="flex items-center gap-1.5 min-h-[22px]">
                <SmileIcon width={16} height={16} className="text-custom-warm-grey-400" />
                <p className="text-body-12-regular text-custom-warm-grey-600">
                  {t('categoryCard.satisfactionScoreText')}
                </p>
                <Tag color="cyan" size="regular" label={`${score}%`} />
              </div>
            )}
          </div>
        </div>
        <Dropdown open={isOpenDropdown} onOpenChange={setIsOpenDropdown}>
          <DropdownMenuTrigger variant="transparent" asChild>
            <IconButton
              icon={<MoreVerticalIcon width={20} height={20} />}
              size="sm"
              variant="teritary"
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuSimpleItem onClick={handleTransaltionClick}>
              <GlobeIcon width={20} height={20} className="text-custom-warm-grey-500" />
              {t('categoryCard.dropdown.translationButton')}
            </DropdownMenuSimpleItem>
            <DropdownMenuSimpleItem onClick={handleEditClick}>
              <EditIcon width={20} height={20} className="text-custom-warm-grey-500" />
              {t('categoryCard.dropdown.editButton')}
            </DropdownMenuSimpleItem>
            <DropdownMenuSeparator />
            <DropdownMenuSimpleItem onClick={handleDeleteClick}>
              <DeleteIcon width={20} height={20} className="text-custom-warm-grey-500" />
              {t('categoryCard.dropdown.deleteButton')}
            </DropdownMenuSimpleItem>
          </DropdownMenuContent>
        </Dropdown>
      </CardContent>
    </Card>
  );
};
